import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ActiveErrorDto,
  CalibrationRunDto,
  CalibrationType,
  CommandDto,
  ConfigDeploymentDto,
  ControlLeaseDto,
  ControlMode,
  ControlOwnerDto,
  DeploymentPreviewDto,
  DiagnosticArtifactDto,
  FleetDto,
  Page,
  RecoveryAttemptDto,
  RobotCredentialDto,
  RobotDto,
  SiteDto,
  TelemetryHistoryDto,
  TelemetryValueDto,
  TimelineDto,
} from "@tcr/contracts";
import { api, isApiError, toItems } from "./client";
import { EP } from "./endpoints";
import { qk } from "./keys";
import { toast } from "@/lib/toast";

// ───────────── sites / fleets / robots ─────────────

export function useSites() {
  return useQuery({ queryKey: qk.sites, queryFn: async () => toItems(await api.get<Page<SiteDto> | SiteDto[]>(EP.sites, { size: 200 })), staleTime: 60_000 });
}
export function useFleets() {
  return useQuery({ queryKey: qk.fleets, queryFn: async () => toItems(await api.get<Page<FleetDto> | FleetDto[]>(EP.fleets, { size: 200 })), staleTime: 60_000 });
}

export function useRobots(params: { siteId?: string; fleetId?: string } = {}) {
  return useQuery({
    queryKey: qk.robots(params),
    queryFn: async () => toItems(await api.get<Page<RobotDto> | RobotDto[]>(EP.robots, { ...params, size: 200 })),
    refetchInterval: 60_000,
  });
}

export function useRobot(id: string | undefined) {
  return useQuery({
    queryKey: qk.robot(id ?? ""),
    queryFn: () => api.get<RobotDto>(EP.robot(id!)),
    enabled: !!id,
    refetchInterval: 60_000,
  });
}

export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; timezone: string }) => api.post<SiteDto>(EP.sites, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sites }),
    onError: (e) => toast.error(e),
  });
}
export function useCreateFleet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { siteId: string; name: string }) => api.post<FleetDto>(EP.fleets, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.fleets }),
    onError: (e) => toast.error(e),
  });
}
export function useCreateRobot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; serial: string; siteId: string; fleetId: string | null; model: string }) =>
      api.post<{ robot: RobotDto; credential: RobotCredentialDto }>(EP.robots, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.robotsAll }),
    onError: (e) => toast.error(e),
  });
}
export function useUpdateRobot(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; fleetId?: string | null; siteId?: string }) => api.patch<RobotDto>(EP.robot(id), body),
    onSuccess: (r) => {
      qc.setQueryData(qk.robot(id), r);
      qc.invalidateQueries({ queryKey: qk.robotsAll });
    },
    onError: (e) => toast.error(e),
  });
}
export function useRotateCredentials(id: string) {
  return useMutation({
    mutationFn: () => api.post<RobotCredentialDto>(EP.robotRotateCredentials(id)),
    onError: (e) => toast.error(e),
  });
}

// ───────────── live sub-resources ─────────────

/** Query options for a robot's latest telemetry (shared by useTelemetry and multi-robot useQueries). */
export function telemetryQuery(robotId: string) {
  return {
    queryKey: qk.robotTelemetry(robotId),
    queryFn: async () => toItems(await api.get<TelemetryValueDto[] | Page<TelemetryValueDto>>(EP.robotTelemetry(robotId))),
    refetchInterval: 30_000,
  };
}

export function useTelemetry(robotId: string) {
  return useQuery(telemetryQuery(robotId));
}

/** Recent metric history (the platform keeps a short in-memory window; see the response's `retention`). */
export function useTelemetryHistory(robotId: string) {
  return useQuery({
    queryKey: qk.robotTelemetryHistory(robotId),
    queryFn: () => api.get<TelemetryHistoryDto>(EP.robotTelemetryHistory(robotId)),
    refetchInterval: 15_000,
  });
}

export function useTimeline(robotId: string, params: { from?: string; to?: string; limit?: number }) {
  return useQuery({
    queryKey: qk.robotTimeline(robotId, params),
    queryFn: () => api.get<TimelineDto>(EP.robotTimeline(robotId), params),
    refetchInterval: 30_000,
  });
}

export function useActiveErrors(robotId: string) {
  return useQuery({
    queryKey: qk.robotErrors(robotId),
    queryFn: async () => toItems(await api.get<ActiveErrorDto[] | Page<ActiveErrorDto>>(EP.robotErrors(robotId))),
    refetchInterval: 60_000,
  });
}

export function useCommands(robotId: string) {
  return useQuery({
    queryKey: qk.robotCommands(robotId),
    queryFn: async () => toItems(await api.get<Page<CommandDto> | CommandDto[]>(EP.robotCommands(robotId), { size: 50 })),
  });
}

export function useInvokeInterface(robotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { interfaceId: string; args: Record<string, unknown>; ttlSec: number }) => api.post<CommandDto>(EP.robotCommands(robotId), body),
    onSuccess: (cmd) => upsertList(qc, qk.robotCommands(robotId), cmd, true),
  });
}

/** Request cancellation; the command's cancel milestones (realtime) say when the robot stopped. */
export function useCancelCommand(robotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commandId: string) => api.post<CommandDto>(EP.commandCancel(commandId)),
    onSuccess: (cmd) => upsertList(qc, qk.robotCommands(robotId), cmd),
    onError: (e) => toast.error(e, "Cancel not sent"),
  });
}

// ───────────── control lease ─────────────

export function useControl(robotId: string) {
  return useQuery({
    queryKey: qk.robotControl(robotId),
    queryFn: async () => (await api.get<ControlOwnerDto | null>(EP.robotControl(robotId))) ?? null,
    refetchInterval: 30_000,
  });
}

function setControl(qc: ReturnType<typeof useQueryClient>, robotId: string, control: ControlOwnerDto | null) {
  qc.setQueryData(qk.robotControl(robotId), control);
  qc.setQueryData<RobotDto>(qk.robot(robotId), (r) => (r ? { ...r, control } : r));
}

export function useRequestControl(robotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { mode: ControlMode; takeover?: boolean; durationSec?: number }) =>
      api.post<ControlLeaseDto>(EP.robotControlLeases(robotId), { takeover: false, durationSec: 300, ...body }),
    onSuccess: (lease) => setControl(qc, robotId, lease),
    onError: (e) => toast.error(e, "Could not take control"),
  });
}

export function useRenewLease(robotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leaseId: string) => api.post<ControlLeaseDto>(EP.controlLeaseRenew(leaseId)),
    onSuccess: (lease) => setControl(qc, robotId, lease),
    onError: (e) => {
      toast.error(e, "Could not renew control");
      qc.invalidateQueries({ queryKey: qk.robotControl(robotId) });
    },
  });
}

export function useReleaseLease(robotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leaseId: string) => api.del(EP.controlLease(leaseId)),
    onSuccess: () => setControl(qc, robotId, null),
    onError: (e) => {
      if (isApiError(e, "NotFound")) setControl(qc, robotId, null);
      else toast.error(e, "Could not release control");
    },
  });
}

// ───────────── e-stop ─────────────

export function useEstop(robotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { action: "engage" | "release"; confirm: boolean }) => api.post<CommandDto | void>(EP.robotEstop(robotId), body),
    onSuccess: (_r, v) => {
      if (v.action === "engage") toast.warning("E-stop engaged", "Stop command sent to the robot.");
      else toast.success("E-stop release requested");
      qc.invalidateQueries({ queryKey: qk.robot(robotId) });
    },
    onError: (e, v) => toast.error(e, v.action === "engage" ? "E-STOP FAILED — use the physical e-stop" : "Could not release e-stop"),
  });
}

// ───────────── calibration / recovery ─────────────

export function useCalibrations(robotId: string) {
  return useQuery({
    queryKey: qk.robotCalibrations(robotId),
    queryFn: async () => toItems(await api.get<CalibrationRunDto[] | Page<CalibrationRunDto>>(EP.robotCalibrations(robotId), { size: 100 })),
  });
}
export function useStartCalibration(robotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (type: CalibrationType) => api.post<CalibrationRunDto>(EP.robotCalibrations(robotId), { type, confirm: true }),
    onSuccess: (run) => upsertList(qc, qk.robotCalibrations(robotId), run, true),
    onError: (e) => toast.error(e, "Calibration not started"),
  });
}

export function useRecoveries(robotId: string) {
  return useQuery({
    queryKey: qk.robotRecoveries(robotId),
    queryFn: async () => toItems(await api.get<RecoveryAttemptDto[] | Page<RecoveryAttemptDto>>(EP.robotRecoveryHistory(robotId), { size: 50 })),
    retry: (n, e) => !isApiError(e, "NotFound") && n < 2,
  });
}
export function useStartRecovery(robotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { errorCode: string; instanceId?: string }) => api.post<RecoveryAttemptDto>(EP.robotRecoveries(robotId), { ...body, confirm: true }),
    onSuccess: (a) => {
      upsertList(qc, qk.robotRecoveries(robotId), a, true);
      toast.success("Recovery started", "Watch the error list and recovery history for the outcome.");
    },
    onError: (e) => toast.error(e, "Recovery not started"),
  });
}

// ───────────── deployments / diagnostics ─────────────

/** What deploying `mapId` would change on the robot (compared with its robot-confirmed active bundle). */
export function useDeploymentPreview(robotId: string | undefined, mapId: string | undefined) {
  return useQuery({
    queryKey: qk.deploymentPreview(robotId ?? "", mapId ?? ""),
    queryFn: () => api.get<DeploymentPreviewDto>(EP.deploymentPreview(robotId!), { mapId }),
    enabled: !!robotId && !!mapId,
  });
}

export function useDeployments(robotId: string | undefined) {
  return useQuery({
    queryKey: qk.robotDeployments(robotId ?? ""),
    queryFn: async () => toItems(await api.get<ConfigDeploymentDto[] | Page<ConfigDeploymentDto>>(EP.robotDeployments(robotId!), { size: 50 })),
    enabled: !!robotId,
  });
}
export function useCreateDeployment(robotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { mapId: string; note: string }) => api.post<ConfigDeploymentDto>(EP.robotDeployments(robotId), { ...body, confirm: true }),
    onSuccess: (d) => upsertList(qc, qk.robotDeployments(robotId), d, true),
    onError: (e) => toast.error(e, "Deployment not created"),
  });
}
export function useRollbackDeployment(robotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deploymentId: string) => api.post<ConfigDeploymentDto>(EP.deploymentRollback(robotId, deploymentId), { confirm: true }),
    onSuccess: (d) => {
      upsertList(qc, qk.robotDeployments(robotId), d, true);
      qc.invalidateQueries({ queryKey: qk.robotDeployments(robotId) });
      toast.success("Rollback queued");
    },
    onError: (e) => toast.error(e, "Rollback failed"),
  });
}

export function useDiagnostics(robotId: string) {
  return useQuery({
    queryKey: qk.robotDiagnostics(robotId),
    queryFn: async () => toItems(await api.get<DiagnosticArtifactDto[] | Page<DiagnosticArtifactDto>>(EP.robotDiagnostics(robotId), { size: 100 })),
  });
}

// ───────────── helpers ─────────────

/** Insert or replace an item (by id) in a cached array. */
export function upsertList<T extends { id: string }>(
  qc: ReturnType<typeof useQueryClient>,
  key: readonly unknown[],
  item: T,
  prepend = false,
): void {
  qc.setQueryData<T[]>(key, (list) => {
    if (!list) return list;
    const i = list.findIndex((x) => x.id === item.id);
    if (i === -1) return prepend ? [item, ...list] : [...list, item];
    const next = list.slice();
    next[i] = { ...list[i]!, ...item };
    return next;
  });
}
